import { isArray, uniq } from "lodash";
import { IAction, IBarChart, IMetadata, IName, IRawPlotlyChart, I2dFigure, ILabel } from "../types";

const isAction = (value: unknown): value is IAction =>
  Boolean(value && typeof value === "object" && isArray((value as IAction).sources));

const areRelatedPaths = (left: IName, right: IName) =>
  left === right || left.startsWith(`${right}.`) || right.startsWith(`${left}.`);

export const hasLoadingDependency = (sources: Array<IName>, metadata?: IMetadata) => {
  if (!metadata?.loading?.length || !sources.length) {
    return false;
  }

  return sources.some((source) => metadata.loading.some((loadingPath) => areRelatedPaths(source, loadingPath)));
};

export const isBarChartLoading = (template: IBarChart, metadata?: IMetadata) => {
  const sources = uniq(
    template.figures.flatMap((figure) => {
      if ("type" in figure && figure.type === "structure") {
        return [figure.name];
      }

      const chartFigure = figure as I2dFigure;
      return [chartFigure.x_data.name, chartFigure.y_data.name];
    })
  );

  return hasLoadingDependency(sources, metadata);
};

export const isRawPlotlyChartLoading = (template: IRawPlotlyChart, metadata?: IMetadata) => {
  const sources = uniq([
    ...(template.dataName ? [template.dataName] : []),
    ...(isAction(template.data) ? template.data.sources : []),
    ...(isAction(template.layout) ? template.layout.sources : []),
  ]);

  return hasLoadingDependency(sources, metadata);
};

export const isLabelLoading = (template: ILabel, metadata?: IMetadata) => {
  const sources = uniq(template.format?.sources?.length ? template.format.sources : [template.name]);
  return hasLoadingDependency(sources, metadata);
};

interface ChartLoadingSkeletonProps {
  className: string;
  height?: number;
  label?: string;
  variant?: "bar" | "plot" | "sankey";
}

export const ChartLoadingSkeleton = ({ className, height, label, variant = "plot" }: ChartLoadingSkeletonProps) => (
  <div
    className={`${className} chart-loading-skeleton chart-loading-skeleton--${variant}`}
    {...(height && { style: { height: `${height}px` } })}
    aria-live="polite"
    aria-busy="true"
  >
    <div className="chart-loading-skeleton__surface" aria-hidden="true">
      <span className="chart-loading-skeleton__title" />
      {variant === "sankey" ? (
        <div className="chart-loading-skeleton__plot chart-loading-skeleton__plot--sankey">
          <span className="chart-loading-skeleton__square" />
        </div>
      ) : (
        <>
          <div className="chart-loading-skeleton__plot">
            <span className="chart-loading-skeleton__axis chart-loading-skeleton__axis--y" />
            <span className="chart-loading-skeleton__axis chart-loading-skeleton__axis--x" />
            <span className="chart-loading-skeleton__grid chart-loading-skeleton__grid--1" />
            <span className="chart-loading-skeleton__grid chart-loading-skeleton__grid--2" />
            <span className="chart-loading-skeleton__grid chart-loading-skeleton__grid--3" />
            {variant === "bar" ? (
              <div className="chart-loading-skeleton__bars">
                <span className="chart-loading-skeleton__bar chart-loading-skeleton__bar--1" />
                <span className="chart-loading-skeleton__bar chart-loading-skeleton__bar--2" />
                <span className="chart-loading-skeleton__bar chart-loading-skeleton__bar--3" />
                <span className="chart-loading-skeleton__bar chart-loading-skeleton__bar--4" />
                <span className="chart-loading-skeleton__bar chart-loading-skeleton__bar--5" />
                <span className="chart-loading-skeleton__bar chart-loading-skeleton__bar--6" />
              </div>
            ) : (
              <div className="chart-loading-skeleton__traces">
                <span className="chart-loading-skeleton__trace chart-loading-skeleton__trace--primary" />
                <span className="chart-loading-skeleton__trace chart-loading-skeleton__trace--secondary" />
              </div>
            )}
          </div>
          <div className="chart-loading-skeleton__legend">
            <span className="chart-loading-skeleton__legend-item chart-loading-skeleton__legend-item--1" />
            <span className="chart-loading-skeleton__legend-item chart-loading-skeleton__legend-item--2" />
            <span className="chart-loading-skeleton__legend-item chart-loading-skeleton__legend-item--3" />
          </div>
        </>
      )}
    </div>
    <span className="chart-loading-skeleton__sr-only">
      {label ? `Loading ${label} chart` : "Loading chart"}
    </span>
  </div>
);
